// TODO: Remove these two lint exceptions
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access */
import { useMemo, useState } from 'react';
import type { FC, LegacyRef } from 'react';
import {
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Select,
    Textarea,
} from '@chakra-ui/react';
import isUndefined from 'lodash-es/isUndefined';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { Text } from '../../ui-components';
import { GetIcon, ViewIcon, ViewOffIcon } from '../icons';
import { fieldTypes, isInputType } from './FieldConfig';
import { Combobox } from './combobox';
import type { FieldConfig } from '.';
import type { AutocompleteAddress } from '../../types/radar';
import type { FieldType } from './FieldConfig';

const Field: FC<FieldConfig> = ({
    name,
    id = name,
    label,
    labelRef,
    ariaLabel = '',
    formLabel,
    autoFocus,
    type = 'text',
    options = [],
    comboboxOptions = [],
    rules = {},
    error,
    placeholder,
    inputProps,
    control,
    defaultValue,
    helpText,
    autoComplete = 'on',
    children,
    className,
    maxWidth,
    isInvalid,
    variant,
    rightIcon,
    leftIcon,
    multiple,
    accept,
    isDisabled = false,
    iconName,
    leftText,
    useStreetAddressOnly,
}) => {
    const [hidePassword, setHidePassword] = useState(true);
    const PasswordIcon = hidePassword ? ViewIcon : ViewOffIcon;
    const isHiddenType = type === 'hidden';
    const isPasswordType = type === 'password';
    const isSelectType = type === 'select';
    const isCheckboxType = type === 'checkbox';
    const isAutocompleteType = type === 'autocomplete';
    const isTextAriaType = type === 'textarea';
    const isFileType = type === 'file';
    // invisible type is for case when input should be hidden but all other attributes applied as for existed input (i.e. error message)
    const isInvisibleType = type === 'invisible';

    let inputType: FieldType;
    if (isPasswordType && hidePassword) {
        inputType = 'password';
    } else {
        inputType = isPasswordType ? 'text' : type;
    }

    const filteredRules = useMemo(
        () =>
            Object.entries(rules)
                .filter(([, value]) => !isUndefined(value))
                .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
        [rules],
    );

    return (
        <Controller
            name={name}
            control={control}
            rules={filteredRules}
            defaultValue={defaultValue}
            render={({ field: { onChange, onBlur, value, ref } }): React.ReactElement => {
                const _inputProps =
                    typeof inputProps === 'function' ? inputProps({ value, onChange }) : inputProps;

                return (
                    <FormControl
                        className={className}
                        id={name}
                        isInvalid={isInvalid || !!error}
                        onBlur={onBlur}
                        display={isHiddenType ? 'none' : 'block'}
                        variant={variant}
                    >
                        {!['checkbox', 'radio'].includes(type) &&
                            type !== 'hidden' &&
                            (formLabel ||
                                (label && (
                                    <FormLabel
                                        ref={labelRef as LegacyRef<HTMLLabelElement>}
                                        htmlFor={id}
                                        data-testid={`${id}-label`}
                                        variant={variant}
                                    >
                                        {label}
                                    </FormLabel>
                                )))}

                        <InputGroup maxWidth={maxWidth}>
                            {isInputType(type) && (
                                <>
                                    {leftIcon && (
                                        <InputLeftElement pointerEvents="none">
                                            <GetIcon name={leftIcon} size="sm" />
                                        </InputLeftElement>
                                    )}
                                    {leftText && (
                                        <InputLeftElement
                                            width="auto"
                                            pointerEvents="none"
                                            left={2}
                                        >
                                            <Text>{leftText}</Text>
                                        </InputLeftElement>
                                    )}
                                    <Input
                                        data-testid={id}
                                        id={id}
                                        name={name}
                                        aria-label={ariaLabel}
                                        ref={ref}
                                        onChange={onChange}
                                        value={value}
                                        type={inputType}
                                        placeholder={placeholder}
                                        autoComplete={autoComplete}
                                        autoFocus={autoFocus}
                                        variant={variant}
                                        {..._inputProps}
                                    />
                                    {rightIcon && (
                                        <InputRightElement
                                            pointerEvents={
                                                _inputProps?.onRightIconClick ? 'auto' : 'none'
                                            }
                                        >
                                            <GetIcon
                                                id={`${id}-right-icon`}
                                                data-testid={`${id}-right-icon`}
                                                name={rightIcon}
                                                size="sm"
                                                onClick={_inputProps?.onRightIconClick}
                                                cursor={
                                                    _inputProps?.onRightIconClick
                                                        ? 'pointer'
                                                        : undefined
                                                }
                                            />
                                        </InputRightElement>
                                    )}
                                </>
                            )}

                            {(isHiddenType || isInvisibleType) && (
                                <input
                                    data-testid={id}
                                    id={id}
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    type="hidden"
                                    {..._inputProps}
                                />
                            )}

                            {isPasswordType && (
                                <InputRightElement>
                                    <IconButton
                                        data-testid={`${id}-icon`}
                                        id={`${id}-icon`}
                                        variant="ghosted"
                                        aria-label={
                                            hidePassword ? 'Show password' : 'Hide password'
                                        }
                                        icon={<PasswordIcon width="16px" />}
                                        onClick={() => setHidePassword(!hidePassword)}
                                    />
                                </InputRightElement>
                            )}

                            {isSelectType && (
                                <Select
                                    data-testid={id}
                                    id={id}
                                    icon={
                                        <GetIcon
                                            name={iconName || 'dropdown-arrow-open'}
                                            size="sm"
                                        />
                                    }
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    placeholder={placeholder}
                                    {..._inputProps}
                                    autoComplete={autoComplete}
                                    isDisabled={isDisabled}
                                    variant={variant}
                                >
                                    {options.map((opt) => (
                                        <option key={`${opt.label}-${opt.value}`} value={opt.value}>
                                            {variant === 'new-style' ? (
                                                <Text size="small">{opt.label}</Text>
                                            ) : (
                                                opt.label
                                            )}
                                        </option>
                                    ))}
                                </Select>
                            )}

                            {isCheckboxType && (
                                <Checkbox
                                    data-testid={id}
                                    id={id}
                                    ref={ref}
                                    onChange={(e): void => onChange(e.target.checked)}
                                    isChecked={value}
                                    {..._inputProps}
                                    variant={variant}
                                >
                                    {formLabel ?? label}
                                </Checkbox>
                            )}

                            {isFileType && (
                                <Input
                                    hidden
                                    data-testid={id}
                                    id={id}
                                    name={name}
                                    aria-label={ariaLabel}
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    type={inputType}
                                    placeholder={placeholder}
                                    autoComplete={autoComplete}
                                    autoFocus={autoFocus}
                                    variant={variant}
                                    multiple={multiple}
                                    accept={accept}
                                    {..._inputProps}
                                />
                            )}
                            {children}
                        </InputGroup>

                        {isAutocompleteType && (
                            <Combobox
                                items={comboboxOptions}
                                handleInputChange={(addressString: string): void => {
                                    // TODO rework _inputProps object to type in proper way
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                                    _inputProps.onChange(addressString);
                                    onChange(addressString);
                                }}
                                handleSelect={(address: AutocompleteAddress): void => {
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                                    _inputProps.handleAutocompleteSelect(address);
                                }}
                                onKeyDown={_inputProps.onKeyDown}
                                inputProps={{ id, name, autoComplete, value }}
                                variant={variant}
                                useStreetAddressOnly={useStreetAddressOnly}
                            />
                        )}

                        {isTextAriaType && (
                            <Textarea
                                value={value}
                                id={id}
                                onChange={onChange}
                                data-testid={id}
                                name={name}
                                ref={ref}
                                aria-label={ariaLabel}
                                placeholder={placeholder}
                                autoComplete={autoComplete}
                                autoFocus={autoFocus}
                                variant={variant}
                                {..._inputProps}
                            />
                        )}

                        {!!error && error.message && type !== 'hidden' && (
                            <FormErrorMessage data-testid={`${id}-error-message`} variant={variant}>
                                {error.message}
                            </FormErrorMessage>
                        )}

                        {helpText ? (
                            <FormHelperText data-testid={`${id}-helper-message`}>
                                {helpText}
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                );
            }}
        />
    );
};

Field.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    label: PropTypes.any,
    labelRef: PropTypes.any,
    ariaLabel: PropTypes.string,
    formLabel: PropTypes.any,
    autoFocus: PropTypes.bool,
    type: PropTypes.oneOf(fieldTypes).isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired])
                .isRequired,
        }).isRequired,
    ),
    comboboxOptions: PropTypes.arrayOf(PropTypes.any.isRequired),
    rules: PropTypes.exact({
        required: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        min: PropTypes.exact({
            value: PropTypes.number.isRequired,
            message: PropTypes.string.isRequired,
        }),
        max: PropTypes.exact({
            value: PropTypes.number.isRequired,
            message: PropTypes.string.isRequired,
        }),
        maxLength: PropTypes.exact({
            value: PropTypes.number.isRequired,
            message: PropTypes.string.isRequired,
        }),
        minLength: PropTypes.exact({
            value: PropTypes.number.isRequired,
            message: PropTypes.string.isRequired,
        }),
        pattern: PropTypes.exact({
            value: PropTypes.instanceOf(RegExp).isRequired,
            message: PropTypes.string.isRequired,
        }),
        validate: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.objectOf(PropTypes.func.isRequired),
        ]),
    }),
    error: PropTypes.shape({
        type: PropTypes.oneOf([
            'required',
            'min',
            'max',
            'maxLength',
            'minLength',
            'pattern',
            'validate',
            'valueAsNumber',
            'valueAsDate',
            'setValueAs',
            'hasBeenSelected',
            'custom',
        ]).isRequired,
        message: PropTypes.string,
    }),
    placeholder: PropTypes.string,
    inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    // defaultValue is a ts generic
    // eslint-disable-next-line react/forbid-prop-types
    defaultValue: PropTypes.any,
    helpText: PropTypes.any,
    autoComplete: PropTypes.string,
    children: PropTypes.any,
    className: PropTypes.string,
    variant: PropTypes.oneOf(['new-style', 'no-margin']),
    useStreetAddressOnly: PropTypes.bool,
};

Field.defaultProps = {
    id: undefined,
    ariaLabel: undefined,
    formLabel: undefined,
    labelRef: undefined,
    autoFocus: false,
    options: [],
    comboboxOptions: [],
    rules: {},
    error: undefined,
    placeholder: undefined,
    inputProps: undefined,
    defaultValue: undefined,
    helpText: undefined,
    autoComplete: undefined,
    children: undefined,
    className: undefined,
    variant: undefined,
    useStreetAddressOnly: false,
};

export { Field };
